/**
 * hw-empty-cart.js
 *
 * Handles two features for the empty cart upsell slider:
 *   1. Drag-to-scroll (mouse) — event delegation, works with drawer's dynamic DOM
 *   2. Quick add to cart — posts to /cart/add.js and fires cart:update to
 *      trigger Horizon's section re-render (cart switches from empty → items)
 */

// ── Drag-to-scroll ────────────────────────────────────────────────────────────

let isDragging = false;
let activeSlider = null;
let startX = 0;
let scrollLeft = 0;

/** @param {EventTarget} target @returns {HTMLElement|null} */
function getSlider(target) {
  return /** @type {HTMLElement} */ (target)?.closest?.(
    '.hw-empty-cart__recommendations--slider'
  ) ?? null;
}

document.addEventListener('mousedown', (e) => {
  const slider = getSlider(e.target);
  if (!slider) return;
  isDragging = true;
  activeSlider = slider;
  startX = e.pageX - slider.offsetLeft;
  scrollLeft = slider.scrollLeft;
  slider.classList.add('hw-ec-dragging');
  e.preventDefault();
});

document.addEventListener('mouseup', () => {
  if (!activeSlider) return;
  activeSlider.classList.remove('hw-ec-dragging');
  isDragging = false;
  activeSlider = null;
});

document.addEventListener('mouseleave', () => {
  if (!activeSlider) return;
  activeSlider.classList.remove('hw-ec-dragging');
  isDragging = false;
  activeSlider = null;
});

document.addEventListener('mousemove', (e) => {
  if (!isDragging || !activeSlider) return;
  e.preventDefault();
  const x = e.pageX - activeSlider.offsetLeft;
  const walk = (x - startX) * 1.5;
  activeSlider.scrollLeft = scrollLeft - walk;
});

// ── Quick Add to Cart ─────────────────────────────────────────────────────────

/**
 * Gather section IDs from all cart-items-component elements in the DOM.
 * These are passed to /cart/add.js so Shopify returns section HTML for
 * the cart-items-component to morph — switching from empty → items state.
 * @returns {string}
 */
function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

/**
 * Set an ATC button into a loading / success / reset state.
 * @param {HTMLButtonElement} btn
 * @param {'loading'|'added'|'reset'} state
 * @param {string} [originalText]
 */
function setAtcButtonState(btn, state, originalText) {
  switch (state) {
    case 'loading':
      btn.disabled = true;
      btn.dataset.originalText = btn.textContent?.trim() ?? '';
      btn.textContent = '…';
      break;
    case 'added':
      btn.textContent = btn.dataset.addedText || '✓';
      break;
    case 'reset':
      btn.disabled = false;
      btn.textContent = originalText || btn.dataset.originalText || btn.textContent;
      break;
  }
}

// ATC button click
document.addEventListener('click', (e) => {
  const btn = /** @type {HTMLButtonElement|null} */ (
    e.target instanceof Element ? e.target.closest('.hw-empty-cart__atc-btn') : null
  );
  if (!btn || btn.disabled) return;
  e.preventDefault();
  e.stopPropagation(); // don't bubble to the card link

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const wrap = btn.closest('.hw-empty-cart__atc-wrap');
  const qtyInput = /** @type {HTMLInputElement|null} */ (
    wrap?.querySelector('.hw-empty-cart__qty-input')
  );
  const qty = Math.max(1, parseInt(qtyInput?.value ?? '1', 10));
  const originalText = btn.textContent?.trim() ?? '';

  setAtcButtonState(btn, 'loading');

  const sectionIds = getCartSectionIds();
  const payload = {
    id: parseInt(variantId, 10),
    quantity: qty,
    ...(sectionIds && { sections: sectionIds }),
  };

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((r) => r.json())
    .then((response) => {
      setAtcButtonState(btn, 'added');

      /**
       * Fire cart:update — cart-items-component morphs the section HTML.
       * If the drawer is in the empty state, wrap the DOM update in a view
       * transition so the empty→items switch animates smoothly (mirrors the
       * existing items→empty transition driven by component-cart-items.js).
       *
       * Check for .hw-empty-cart presence (not .cart-drawer--empty on the dialog)
       * because .cart-drawer--empty is only set server-side; after the JS
       * items→empty transition it's absent, so the transition would never fire
       * on subsequent adds.
       */
      const isEmptyDrawer = !!document.querySelector('.cart-drawer__dialog .hw-empty-cart');
      const supportsVT = typeof document.startViewTransition === 'function';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const fireUpdate = () => {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { sections: response.sections ?? {} } },
          })
        );
      };

      if (isEmptyDrawer && supportsVT && !reducedMotion) {
        const transition = document.startViewTransition(fireUpdate);
        try { transition.types.add('fill-cart-drawer'); } catch (_) { /* level-2 types not yet in all browsers */ }
      } else {
        fireUpdate();
      }

      // Reset button after short delay
      setTimeout(() => setAtcButtonState(btn, 'reset', originalText), 1500);
    })
    .catch((err) => {
      console.error('[hw-empty-cart] Add to cart failed:', err);
      setAtcButtonState(btn, 'reset', originalText);
    });
});

// Quantity +/− buttons
document.addEventListener('click', (e) => {
  const qtyBtn = /** @type {HTMLButtonElement|null} */ (
    e.target instanceof Element ? e.target.closest('.hw-empty-cart__qty-btn') : null
  );
  if (!qtyBtn) return;
  e.stopPropagation();

  const input = /** @type {HTMLInputElement|null} */ (
    qtyBtn.closest('.hw-empty-cart__qty')?.querySelector('.hw-empty-cart__qty-input')
  );
  if (!input) return;

  const current = parseInt(input.value, 10);
  const action = qtyBtn.dataset.action;
  if (action === 'increase') input.value = String(current + 1);
  if (action === 'decrease' && current > 1) input.value = String(current - 1);
});
